import React, { useState, useEffect, useMemo } from "react";
import LZString from "lz-string"; // Import LZString for decompression
import "./inflmain.css";

const API_BASE_URL = process.env.NODE_ENV === 'development' 
    ? 'http://127.0.0.1:8080'
    : 'https://bymolive-backend-995926394015.us-central1.run.app';

const InflPrograms = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [isFetching, setIsFetching] = useState(false);
    const [lastFetchTime, setLastFetchTime] = useState(null);
    const [loadingError, setLoadingError] = useState(null);
    const [formsData, setFormsData] = useState([]);
    const [ordersData, setOrdersData] = useState([]);
    const [availablePrograms, setAvailablePrograms] = useState([]);
    const [selectedPrograms, setSelectedPrograms] = useState([]);
    const [totalPersonalOrders, setTotalPersonalOrders] = useState({ amount: 0, count: 0 });
    const [totalDiscountOrders, setTotalDiscountOrders] = useState({ amount: 0, count: 0 });
    const [totalNet, setTotalNet] = useState(0);

    // Financial statuses to consider as valid (paid or pending)
    const validFinancialStatuses = [
        "paid", "partially_paid", "pending", "authorized"
    ];

    // Initial data loading on component mount
    useEffect(() => {
        console.log("InflPrograms component mounted, checking for cached data");
        
        // Load orders data from sessionStorage
        loadOrdersData();
        
        // Try to load forms from sessionStorage first
        const cachedForms = sessionStorage.getItem('programFormsData');
        const lastFetchTimeStr = sessionStorage.getItem('programFormsLastFetch');
        
        if (cachedForms && lastFetchTimeStr) {
            console.log("Found cached forms data, processing");
            try {
                const forms = JSON.parse(cachedForms);
                setLastFetchTime(new Date(lastFetchTimeStr));
                processFormsData(forms);
            } catch (error) {
                console.error("Error processing cached forms:", error);
                setLoadingError("Failed to process cached data: " + error.message);
                fetchFormsData(); // Try fetching fresh data if cache processing fails
            }
        } else {
            console.log("No cached data found, fetching fresh data");
            fetchFormsData();
        }
    }, []);

    // Process forms data to extract programs and sort by date
    const processFormsData = (forms) => {
        try {
            // Extract unique programs
            const programsSet = new Set();
            forms.forEach(form => {
                if (form.program) {
                    programsSet.add(form.program);
                } else {
                    programsSet.add("Unknown");
                }
            });
            
            const programs = Array.from(programsSet).sort();
            setAvailablePrograms(programs);
            
            // If no programs are selected, select all by default
            if (selectedPrograms.length === 0) {
                setSelectedPrograms(programs);
            }
            
            // Sort forms by creation date (newest first)
            const sortedForms = [...forms].sort((a, b) => {
                const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
                const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
                return dateB - dateA; // Descending order
            });
            
            setFormsData(sortedForms);
            setIsLoading(false);
        } catch (error) {
            console.error("Error processing forms data:", error);
            setLoadingError("Error processing forms data: " + error.message);
        }
    };

    // Load orders data from sessionStorage
    const loadOrdersData = () => {
        try {
            const compressedData = sessionStorage.getItem("rawOrdersData");
            if (compressedData) {
                console.log("Found compressed orders data, decompressing...");
                const decompressedData = JSON.parse(LZString.decompress(compressedData));
                console.log(`Decompressed ${decompressedData.length} orders`);
                
                // Filter orders to include only paid or pending
                const filteredOrders = decompressedData.filter(order => 
                    order.financial_status && 
                    validFinancialStatuses.includes(order.financial_status)
                );
                
                console.log(`Filtered to ${filteredOrders.length} paid/pending orders`);
                setOrdersData(filteredOrders || []);
            } else {
                console.warn("No compressed orders data found in sessionStorage");
                setOrdersData([]);
            }
        } catch (error) {
            console.error("Error loading orders data:", error);
            setOrdersData([]);
        }
    };

    // Function to fetch forms data directly from the API
    const fetchFormsData = async () => {
        console.log("Fetching forms data from API");
        setIsLoading(true);
        setLoadingError(null);
        setIsFetching(true);
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/get-all-forms`);
            if (!response.ok) {
                throw new Error(`API request failed with status ${response.status}`);
            }
            
            const data = await response.json();
            console.log("Forms data fetched successfully:", data);
            
            if (data && data.forms && Array.isArray(data.forms)) {
                // Save to sessionStorage
                sessionStorage.setItem('programFormsData', JSON.stringify(data.forms));
                
                // Update last fetch time
                const now = new Date();
                sessionStorage.setItem('programFormsLastFetch', now.toISOString());
                setLastFetchTime(now);
                
                processFormsData(data.forms);
            } else {
                throw new Error("Invalid response format from forms API");
            }
        } catch (error) {
            console.error("Error fetching forms data:", error);
            setLoadingError("Failed to fetch forms data: " + error.message);
            setIsLoading(false);
        } finally {
            setIsFetching(false);
        }
    };

    // Function to format date string
    const formatDate = (dateString) => {
        if (!dateString) return "Unknown";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch (error) {
            console.warn("Error formatting date:", error);
            return dateString;
        }
    };

    // Function to find orders for a given email (personal orders)
    const getOrdersForEmail = (email) => {
        if (!email || !ordersData.length) return [];
        
        // Filter orders that match the email
        return ordersData.filter(order => 
            order.customer && 
            order.customer.email && 
            order.customer.email.toLowerCase() === email.toLowerCase()
        );
    };
    
    // Function to find orders with a specific discount code, excluding personal orders
    const getOrdersForDiscountCode = (code, influencerEmail) => {
        if (!code || !ordersData.length) return [];
        
        const normCode = code.toUpperCase();
        const normEmail = influencerEmail ? influencerEmail.toLowerCase() : null;
        
        // Filter orders that use the discount code but weren't made by the influencer
        return ordersData.filter(order => {
            // Check if order has discount codes
            if (!order.discount_codes || !Array.isArray(order.discount_codes)) return false;
            
            // Check if any discount code matches
            const hasMatchingCode = order.discount_codes.some(
                discount => discount.code && discount.code.toUpperCase() === normCode
            );
            
            // If it has matching code, make sure it's not from the influencer's email
            if (hasMatchingCode && normEmail) {
                return !(order.customer && 
                         order.customer.email && 
                         order.customer.email.toLowerCase() === normEmail);
            }
            
            return hasMatchingCode;
        });
    };

    // Toggle a program in the selected programs list
    const toggleProgram = (program) => {
        if (selectedPrograms.includes(program)) {
            setSelectedPrograms(selectedPrograms.filter(p => p !== program));
        } else {
            setSelectedPrograms([...selectedPrograms, program]);
        }
    };

    // Select all programs
    const selectAllPrograms = () => {
        setSelectedPrograms([...availablePrograms]);
    };

    // Clear all programs
    const clearAllPrograms = () => {
        setSelectedPrograms([]);
    };

    // Compute filtered forms based on selected programs
    const filteredForms = useMemo(() => {
        if (!selectedPrograms.length) return [];
        
        return formsData.filter(form => {
            const program = form.program || "Unknown";
            return selectedPrograms.includes(program);
        });
    }, [formsData, selectedPrograms]);

    // Calculate totals when filtered forms change
    useEffect(() => {
        // Calculate totals for filtered forms
        let personalTotal = { amount: 0, count: 0 };
        let discountTotal = { amount: 0, count: 0 };
        let netTotal = 0;
        
        // Only run the calculations if we have filtered forms
        if (filteredForms && filteredForms.length > 0) {
            filteredForms.forEach(form => {
                // Calculate personal orders
                const emailOrders = getOrdersForEmail(form.email);
                const personalAmount = emailOrders.reduce(
                    (sum, order) => sum + parseFloat(order.current_total_price || order.total_price || 0),
                    0
                );
                
                // Calculate discount code orders
                const discountOrders = getOrdersForDiscountCode(form.code, form.email);
                const discountAmount = discountOrders.reduce(
                    (sum, order) => sum + parseFloat(order.current_total_price || order.total_price || 0), 
                    0
                );
                
                // Add to totals
                personalTotal.amount += personalAmount;
                personalTotal.count += emailOrders.length;
                discountTotal.amount += discountAmount;
                discountTotal.count += discountOrders.length;
                netTotal += (discountAmount - personalAmount);
            });
        }
        
        // Update state with calculated totals
        setTotalPersonalOrders(personalTotal);
        setTotalDiscountOrders(discountTotal);
        setTotalNet(netTotal);

    }, [ordersData, formsData, selectedPrograms]); // Only depend on these values, not filtered forms

    // Refresh data handler
    const handleRefreshData = () => {
        console.log("Refresh data requested");
        fetchFormsData();
    };

    // Display error state if there's a loading error
    if (loadingError) {
        return (
            <div className="infl-container">
                <div className="error-message">
                    <h3>Error Loading Data</h3>
                    <p>{loadingError}</p>
                    <button 
                        className="refresh-button" 
                        onClick={handleRefreshData}
                        disabled={isFetching}
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // Display loading state
    if (isLoading) {
        return (
            <div className="loading-indicator">
                Loading program data...
                <div className="loading-spinner"></div>
            </div>
        );
    }

    return (
        <div className="infl-container">
            <div className="infl-header-container">
                <h2 className="infl-header">Programs Directory</h2>
                <div className="infl-controls">
                    <div className="last-fetch-info">
                        {lastFetchTime ? (
                            `Last updated: ${new Date(lastFetchTime).toLocaleString()}`
                        ) : (
                            "Data freshly loaded"
                        )}
                    </div>
                    <button 
                        className="refresh-button" 
                        onClick={handleRefreshData}
                        disabled={isFetching}
                    >
                        {isFetching ? "Refreshing..." : "Refresh Data"}
                    </button>
                </div>
            </div>
            
            {/* Program Filter */}
            <div className="program-filter-container">
                <h3>Filter by Program</h3>
                <div className="program-filter-buttons">
                    <button 
                        className="filter-button select-all"
                        onClick={selectAllPrograms}
                    >
                        Select All
                    </button>
                    <button 
                        className="filter-button clear-all"
                        onClick={clearAllPrograms}
                    >
                        Clear All
                    </button>
                </div>
                <div className="program-checkbox-container">
                    {availablePrograms.map(program => (
                        <label key={program} className="program-checkbox-label">
                            <input
                                type="checkbox"
                                checked={selectedPrograms.includes(program)}
                                onChange={() => toggleProgram(program)}
                            />
                            {program}
                        </label>
                    ))}
                </div>
            </div>
            
            {filteredForms.length === 0 ? (
                <div className="no-data-message">No matching programs found.</div>
            ) : (
                <table className="infl-table">
                    <thead>
                        <tr>
                            <th className="table-header"></th>
                            <th className="table-header"></th>
                            <th className="table-header"></th>
                            <th className="table-header"></th>
                            <th className="table-header-top">
                                {Math.round(totalPersonalOrders.amount).toLocaleString()} RON
                                <br />
                                <small>📦 {totalPersonalOrders.count} orders</small>
                            </th>
                            <th className="table-header-top">
                                {Math.round(totalDiscountOrders.amount).toLocaleString()} RON
                                <br />
                                <small>📦 {totalDiscountOrders.count} orders</small>
                            </th>
                            <th className={`table-header-top ${totalNet >= 0 ? 'positive-value' : 'negative-value'}`}>
                                {Math.round(totalNet).toLocaleString()} RON
                            </th>
                        </tr>
                        <tr>
                            <th className="table-header-left">Created Date</th>
                            <th className="table-header-left">Instagram Profile</th>
                            <th className="table-header-left">Discount Code</th>
                            <th className="table-header-left">Program</th>
                            <th className="table-header-left">Personal Orders</th>
                            <th className="table-header-left">Orders</th>
                            <th className="table-header-left">Net</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredForms.map((form, index) => {
                            // Get personal orders (by email)
                            const emailOrders = getOrdersForEmail(form.email);
                            const totalPersonalAmount = emailOrders.reduce(
                                (sum, order) => sum + parseFloat(order.current_total_price || order.total_price || 0), 
                                0
                            );
                            
                            // Get discount code orders (excluding personal orders)
                            const discountOrders = getOrdersForDiscountCode(form.code, form.email);
                            const totalDiscountAmount = discountOrders.reduce(
                                (sum, order) => sum + parseFloat(order.total_price || 0), 
                                0
                            );
                            
                            // Calculate net value
                            const netValue = totalDiscountAmount - totalPersonalAmount;
                            
                            return (
                                <tr key={index}>
                                    <td className="date-cell-left">
                                        {formatDate(form.createdAt)}
                                    </td>
                                    <td className="instagram-cell-left">
                                        {form.profileName ? (
                                            <a 
                                                href={`https://instagram.com/${form.profileName}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                @{form.profileName}
                                            </a>
                                        ) : (
                                            <span className="no-profile">No profile</span>
                                        )}
                                    </td>
                                    <td className="code-cell">
                                        {form.code}
                                        {form.email && (
                                            <div className="email-info">{form.email}</div>
                                        )}
                                    </td>
                                    <td className="program-cell-left">
                                        {form.program || "Unknown"}
                                    </td>
                                    <td className="orders-cell-left">
                                        {emailOrders.length > 0 ? (
                                            <>
                                                <strong>
                                                    {Math.round(totalPersonalAmount).toLocaleString()} RON
                                                </strong>
                                                <br />
                                                <small>📦 {emailOrders.length} orders</small>
                                            </>
                                        ) : (
                                            <span className="no-orders">No orders</span>
                                        )}
                                    </td>
                                    <td className="discount-orders-cell-left">
                                        {discountOrders.length > 0 ? (
                                            <>
                                                <strong>
                                                    {Math.round(totalDiscountAmount).toLocaleString()} RON
                                                </strong>
                                                <br />
                                                <small>📦 {discountOrders.length} orders</small>
                                            </>
                                        ) : (
                                            <span className="no-orders">No orders</span>
                                        )}
                                    </td>
                                    <td className={`net-cell-left ${netValue >= 0 ? 'positive-value' : 'negative-value'}`}>
                                        <strong>
                                            {Math.round(netValue).toLocaleString()} RON
                                        </strong>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default InflPrograms; 